import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { appRoutes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withInterceptors
} from '@angular/common/http';
import { authInterceptor } from './core/auth/auth.interceptor';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { MatDialogModule } from '@angular/material/dialog';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatNativeDateModule } from '@angular/material/core';
import { provideIcons } from './core/icons/icons.provider';
import { provideLuxon } from './core/luxon/luxon.provider';
import { provideVex } from '@vex/vex.provider';
import { provideNavigation } from './core/navigation/navigation.provider';
import { vexConfigs } from '@vex/config/vex-configs';
import { provideQuillConfig } from 'ngx-quill';
import { OAuthModule, OAuthService } from 'angular-oauth2-oidc';
import { MsalModule, MsalService, MsalBroadcastService, MsalGuard } from '@azure/msal-angular';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { env } from 'src/environments/environment';

// Configuración MSAL para Microsoft OAuth (Entra ID)
// ✅ Configuración oficial proporcionada por infraestructura
const msalInstance = new PublicClientApplication({
  auth: {
    clientId: env.module.authAzure.clientAzureId, // c41f9b6f-1385-40c2-9225-854c49f651d1
    authority: `https://login.microsoftonline.com/${env.module.authAzure.tenantAzureId}`, // common (multitenant)
    redirectUri: env.module.authAzure.redirectUri, // http://localhost:4200/auth/microsoft/callback
    postLogoutRedirectUri: env.module.authAzure.postLogoutRedirectUri // http://localhost:4200/login
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true
  }
});

// ⚠️ IMPORTANTE: Inicializar MSAL antes de usar cualquier API
msalInstance.initialize().then(() => {
  console.log('✅ MSAL inicializado correctamente');
}).catch((error) => {
  console.error('❌ Error al inicializar MSAL:', error);
});

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      BrowserModule,
      MatDialogModule,
      MatBottomSheetModule,
      MatNativeDateModule,
      OAuthModule.forRoot(),
      MsalModule.forRoot(msalInstance, 
        {
          interactionType: InteractionType.Redirect, // ✅ Cambio de Popup a Redirect para evitar errores CORS/COOP
          authRequest: {
            scopes: ['openid', 'profile', 'email', 'offline_access'] // ✅ Scopes oficiales de infraestructura
          }
        }, 
        {
          interactionType: InteractionType.Redirect, // ✅ Cambio de Popup a Redirect
          protectedResourceMap: new Map([
            ['https://graph.microsoft.com/v1.0/me', ['User.Read']]
          ])
        }
      )
    ),
    provideRouter(
      appRoutes,
      // TODO: Add preloading withPreloading(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled'
      })
    ),
    provideAnimations(),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([authInterceptor])
    ),

    provideVex({
      /**
       * The config that will be used by default.
       * This can be changed at runtime via the config panel or using the VexConfigService.
       */
      config: vexConfigs.hermes,
      /**
       * Only themes that are available in the config in tailwind.config.ts should be listed here.
       * Any theme not listed here will not be available in the config panel.
       */
      availableThemes: [
        {
          name: 'Default',
          className: 'vex-theme-default'
        },
        {
          name: 'Teal',
          className: 'vex-theme-teal'
        },
        {
          name: 'Green',
          className: 'vex-theme-green'
        },
        {
          name: 'Purple',
          className: 'vex-theme-purple'
        },
        {
          name: 'Red',
          className: 'vex-theme-red'
        },
        {
          name: 'Orange',
          className: 'vex-theme-orange'
        }
      ]
    }),
    provideNavigation(),
    provideIcons(),
    provideLuxon(),
    provideQuillConfig({
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['clean'],
          ['link', 'image']
        ]
      }
    }),
    // Proveedores MSAL para Microsoft OAuth
    MsalService,
    MsalBroadcastService,
    MsalGuard
  ]
};
